import { alpha } from '@mui/material'
import { QueryClient } from '@tanstack/react-query'
import { get } from 'lodash'

import { EIconShape, EPopupDevices } from '~/features/agents/models'
import { queryKeys as agentQueryKeys } from '~/features/agents/utils'
import { PREFIX_EMBEDDED_CHAT } from '~/features/chatbot/constants'
import {
  EEmbeddedChatMessageType,
  TChatMessage,
  TChatTextMessage,
  TEmbeddedChatMessage,
  TEmbeddedChatMessageData,
} from '~/features/chatbot/models'
import defaultChatBotLayout from '~/features/settings/components/chatBot/utils'
import {
  EChatbotTheme,
  TChatbotSettingsLayout,
} from '~/features/settings/models'
import { EClientPath } from '~/models/api'

export function isPreChatFormSubmissionMessage(
  message: TChatMessage,
): message is Extract<TChatMessage, { preChatFormSubmission: unknown }> {
  return message.preChatFormSubmission != null
}

export function isTextChatMessage(
  message: TChatMessage,
): message is TChatTextMessage {
  return !isPreChatFormSubmissionMessage(message)
}

export function shouldActivateButton(
  aiResponse: string,
  assistantMessages: TChatMessage[],
) {
  const phrasesToActivateBookAMeetingBtn = [
    'calendly',
    'schedule a meeting',
    'book a meeting',
    'set up a meeting',
    'reserve a meeting',
  ]
  const lowerCaseResponse = aiResponse.toLowerCase()
  const foundInAiResponse = phrasesToActivateBookAMeetingBtn.some((phrase) =>
    lowerCaseResponse.includes(phrase.toLowerCase()),
  )

  if (foundInAiResponse) {
    return true
  }

  const foundInAssistantMessages = assistantMessages.some((messageObj) => {
    if (!isTextChatMessage(messageObj)) {
      return false
    }

    return phrasesToActivateBookAMeetingBtn.some((phrase) =>
      messageObj.message.toLowerCase().includes(phrase.toLowerCase()),
    )
  })

  return foundInAssistantMessages
}

type TGuestAgentLookupIdentifiers = {
  agentId: string
  friendlyUrl: string
  legacyChatbotId: string
}

export const getGuestAgentLookupIdentifiers = ({
  agentId,
  legacyChatbotId,
  friendlyUrl,
  pathname,
}: {
  agentId?: string
  legacyChatbotId?: string
  friendlyUrl?: string
  pathname: string
}): TGuestAgentLookupIdentifiers => ({
  agentId: agentId ?? '',
  legacyChatbotId: legacyChatbotId ?? '',
  friendlyUrl:
    !!agentId || !!legacyChatbotId
      ? ''
      : (friendlyUrl ?? pathname.replaceAll('/', '')),
})

export const invalidateGuestAgentLookupQueries = (
  queryClient: QueryClient,
  { agentId, friendlyUrl, legacyChatbotId }: TGuestAgentLookupIdentifiers,
) => {
  const queryKeysToInvalidate = [
    ...(agentId ? [agentQueryKeys.guestAgent(agentId)] : []),
    ...(friendlyUrl ? [agentQueryKeys.agents({ friendlyUrl })] : []),
    ...(legacyChatbotId ? [agentQueryKeys.agents({ legacyChatbotId })] : []),
  ]

  return Promise.all(
    queryKeysToInvalidate.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )
}

export const sendMessageToParent = ({
  data,
  targetOrigin = '*',
  type,
}: {
  data?: TEmbeddedChatMessageData
  targetOrigin?: string
  type: EEmbeddedChatMessageType
}) => {
  const messageId = `${PREFIX_EMBEDDED_CHAT}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

  const message: TEmbeddedChatMessage = {
    id: messageId,
    type,
    data,
    timestamp: Date.now(),
  }

  window.parent.postMessage(message, targetOrigin)
}

export const sendMinimizeChange = (messageData: {
  height: number
  isMinimized: boolean
}) =>
  sendMessageToParent({
    data: messageData,
    type: EEmbeddedChatMessageType.minimize,
  })

export const sendCloseChat = () =>
  sendMessageToParent({ type: EEmbeddedChatMessageType.close })

export const postRemountEmbeddedChatMessage = () => {
  const messageId = `${PREFIX_EMBEDDED_CHAT}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

  const message: TEmbeddedChatMessage = {
    id: messageId,
    type: EEmbeddedChatMessageType.remount,
    timestamp: Date.now(),
  }

  window.postMessage(message, window.location.origin)
}

/** Mirrors removeEmbeddedChatbot() in public/js/chatbot-embed.js */
export const removeEmbeddedChatbot = () => {
  document.getElementById('chatbot-container')?.remove()
  document.getElementById('chatbot-icon')?.remove()
  document.querySelector('#chatbot-popup')?.remove()

  const win = window as Window & { __chatbotPopupLoaded?: boolean }
  delete win.__chatbotPopupLoaded
}

export const sendLoadPopup = (data: {
  autoClosePopupDelay?: number
  customIconUrl?: string
  openPopupDelay: number
  popupDevices: EPopupDevices[]
  popupMessage: string
}) => sendMessageToParent({ data, type: EEmbeddedChatMessageType.loadPopup })

export const sendLoadIconStyles = (data: {
  iconBackgroundColor?: string
  iconColor?: string
  iconShape?: EIconShape | null
}) =>
  sendMessageToParent({ data, type: EEmbeddedChatMessageType.loadIconStyles })

export const isValidParentMessage = (
  message: unknown,
): message is TEmbeddedChatMessage => {
  if (!message || typeof message !== 'object') {
    return false
  }

  const msg = message as Record<string, unknown>

  if (!msg.id || typeof msg.id !== 'string') {
    return false
  }

  if (!msg.id.startsWith(PREFIX_EMBEDDED_CHAT)) {
    return false
  }

  if (!msg.type || typeof msg.type !== 'string') {
    return false
  }

  if (typeof msg.timestamp !== 'number') {
    return false
  }

  return true
}

export const isChatbotRoutePath = (path: string) => {
  const chatBasePaths = [EClientPath.chatBase, EClientPath.oldChatBase]
  return chatBasePaths.some((basePath) => path.startsWith(`${basePath}/`))
}

export const isValidAccentColor = (color: string) => {
  try {
    alpha(color, 1)
    return true
  } catch {
    return false
  }
}

export const colorToRgb = ({
  color,
  defaultColor,
}: {
  color: string
  defaultColor: string
}) => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (ctx == null) return defaultColor

  ctx.fillStyle = color
  const rgb = ctx.fillStyle
  return rgb
}

export const getChatbotAccentColor = ({
  layout,
  selectedTheme,
}: {
  layout: TChatbotSettingsLayout
  selectedTheme: EChatbotTheme
}): {
  accentColor: string
  accentColorFieldName: string
  actualValue: string
} => {
  const linkViewPaletteSelected = get(
    layout,
    `selection.${EChatbotTheme.linkView}`,
  )
  const accentColorFieldName = {
    [EChatbotTheme.simple]: `selection.${EChatbotTheme.simple}`,
    [EChatbotTheme.linkView]: `options.${EChatbotTheme.linkView}.${linkViewPaletteSelected}.mainBackgroundColor`,
  }[selectedTheme]

  const accentColor = get(layout, accentColorFieldName)
  const defaultColor = defaultChatBotLayout.selection[EChatbotTheme.simple]

  return {
    accentColor:
      !isValidAccentColor(accentColor) || !accentColor
        ? defaultColor
        : accentColor,
    accentColorFieldName,
    actualValue: accentColor ?? '',
  }
}
